import { Typography } from '@navi/web-ui/lib/primitives';
import NaviTable from '@cp/src/components/NaviTable/NaviTable';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '@cp/src/store';
import { PaginationState } from '@tanstack/react-table';
import { useEffect, useMemo } from 'react';
import { getQcAuditDetails, getQcTeamLeadAudits, getQcTeamLeadFilters } from '../action';
import { setSelectedAuditAssignmentReferenceId } from '../callAuditSlice';
import FilterDrawer from '@cp/src/components/FilterDrawer';
import { navigateToCaseDetails } from '@cp/src/utils/commonUtils';
import APP_ROUTES from '@cp/src/layout/Routes';
import { useNavigate } from 'react-router-dom';
import { getTeamLeadColumns } from './columns';
import { DEFAULT_PAGINATION_STATE } from '../../ExternalDashboardSensei/constants';
import { QcAgentAudit } from '../interface';
import { createQueryParams, readQueryParams } from '@cp/src/utils/QueryParamsHelper';

const TeamLeadView = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const loading = useSelector((state: RootState) => state.callAudit?.isTableLoading);

  const pageSize = useSelector((state: RootState) => state.callAudit?.pageSize);

  const qcAgentAudits = useSelector((state: RootState) => state.callAudit?.qcAgentAudits);
  const displayTotalElements = useSelector((state: RootState) => state.callAudit?.totalElements);
  const qcTeamLeadFilters = useSelector((state: RootState) => state.callAudit?.qcTeamLeadFilters);

  useEffect(() => {
    const { filters, NaviTable } = readQueryParams();
    const qcAgentFilter = filters?.qcAgent?.split(',');
    const auditStatusFilter = filters?.auditStatus?.split(',');
    const page = Number(NaviTable?.pageIndex) || DEFAULT_PAGINATION_STATE.pageIndex;
    const size = Number(NaviTable?.pageSize) || DEFAULT_PAGINATION_STATE.pageSize;

    dispatch(getQcTeamLeadAudits({ page, size, qcAgentFilter, auditStatusFilter }));
    dispatch(getQcTeamLeadFilters());
  }, []);

  const columns = useMemo(() => getTeamLeadColumns(), []);

  const handlePaginationChange = (paginationState: PaginationState) => {
    const newPage = paginationState.pageIndex;
    const newSize = paginationState.pageSize;
    const { filters } = readQueryParams();
    const qcAgentFilter = filters?.qcAgent?.split(',');
    const auditStatusFilter = filters?.auditStatus?.split(',');
    dispatch(
      getQcTeamLeadAudits({ page: newPage, size: newSize, qcAgentFilter, auditStatusFilter })
    );
  };

  const filterSchema = useMemo(
    () => ({
      qcAgent: {
        label: 'Audit Agent',
        options:
          qcTeamLeadFilters?.qcAgents?.map(agent => ({
            label: agent.agentName,
            value: agent.agentReferenceId
          })) || [],
        key: 'qcAgent',
        multiSelect: true,
        isSearchEnabled: false
      },
      auditStatus: {
        label: 'Audit Status',
        options:
          qcTeamLeadFilters?.auditStatuses?.map(status => ({
            label: status.displayName,
            value: status.status
          })) || [],
        key: 'auditStatus',
        multiSelect: true,
        isSearchEnabled: false
      }
    }),
    [qcTeamLeadFilters]
  );

  const handleApplyFilters = (
    selectedFilters: Record<string, string[] | Record<string, string[]>>
  ) => {
    const qcAgentFilter = selectedFilters?.qcAgent as string[] | undefined;
    const auditStatusFilter = selectedFilters?.auditStatus as string[] | undefined;

    const currentParams = readQueryParams();
    const url = createQueryParams({
      ...currentParams,
      NaviTable: { ...currentParams.NaviTable, pageIndex: '0' }
    });
    navigate(url, { replace: true });

    dispatch(
      getQcTeamLeadAudits({
        page: 0,
        size: pageSize,
        qcAgentFilter,
        auditStatusFilter
      })
    );
  };

  const onCasesRowClicked = (data: QcAgentAudit) => {
    dispatch(setSelectedAuditAssignmentReferenceId(data.auditAssignmentReferenceId));
    dispatch(getQcAuditDetails(data.auditAssignmentReferenceId));

    navigateToCaseDetails(
      data?.customerReferenceId,
      data?.caseEntityId,
      data?.caseReferenceId,
      data?.businessVertical,
      navigate,
      APP_ROUTES.CASE_DETAIL.path,
      `mode*callAudit=true&auditId*callAudit=${data.auditAssignmentReferenceId}`
    );
  };

  return (
    <div className="flex flex-col justify-center w-full">
      <div className="flex flex-row justify-between mb-4 items-center">
        <Typography variant="h2" color="var(--navi-color-gray-c1)">
          Calls
        </Typography>

        <FilterDrawer
          filterButtonTitle="Filter"
          filterSchema={filterSchema}
          onApply={handleApplyFilters}
          tableName="CALL_AUDIT"
        />
      </div>

      <NaviTable
        data={qcAgentAudits}
        columns={columns}
        totalElements={displayTotalElements}
        showPaginationComponent={true}
        defaultPaginationState={DEFAULT_PAGINATION_STATE}
        onPaginationChange={handlePaginationChange}
        loading={loading}
        enableRowClick={() => true}
        onRowClick={onCasesRowClicked}
        bodyRowStyles={() => 'h-[52px]'}
        alternateRowColor="var(--greyscale-content-4)"
      />
    </div>
  );
};

export default TeamLeadView;
